import { useState, useEffect } from "react";
import { FileText, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { analytics } from "@/hooks/useAnalytics";

const leadSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required").max(100, "Full name too long"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email too long"),
});

const LEAD_COOKIE_KEY = "itdukes_lead_captured";

export const InsightsLeadModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ fullName?: string; email?: string }>({});
  const { toast } = useToast();

  useEffect(() => {
    const hasSubmitted = localStorage.getItem(LEAD_COOKIE_KEY);
    if (!hasSubmitted) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        analytics.leadFormOpened("insights_modal");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (honeypot) {
      localStorage.setItem(LEAD_COOKIE_KEY, "true");
      toast({
        title: "Thanks for subscribing!",
        description: "You now have full access to the insights.",
      });
      setIsOpen(false);
      return;
    }

    const result = leadSchema.safeParse({ fullName, email });
    if (!result.success) {
      const fieldErrors: { fullName?: string; email?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === "fullName") fieldErrors.fullName = err.message;
        if (err.path[0] === "email") fieldErrors.email = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);

    try {
      const path = window.location.pathname.replace(/\/+$/, "") || "/";
      let source: string;
      if (path === "/") source = "home";
      else if (path === "/tools") source = "tools";
      else if (path.startsWith("/insights/")) source = `insight:${path.slice("/insights/".length)}`;
      else if (path.startsWith("/tools/")) source = `tool:${path.slice("/tools/".length)}`;
      else source = path.replace(/^\//, "");

      const { data, error } = await supabase.functions.invoke("submit-lead", {
        body: {
          fullName: result.data.fullName,
          email: result.data.email,
          source,
          page_url: window.location.href,
          referrer: document.referrer || "",
          honeypot: honeypot,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      localStorage.setItem(LEAD_COOKIE_KEY, "true");
      analytics.leadFormSubmitted("insights_modal");
      
      toast({
        title: "Thanks for subscribing!",
        description: "You now have full access to all insights.",
      });

      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/60 backdrop-blur-sm" />
      
      <div className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl border border-border overflow-hidden animate-scale-in">
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-transparent" />
        
        <div className="relative p-6 sm:p-8">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mb-5">
            <FileText className="w-7 h-7 text-cyan-600" />
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Unlock Research Insights
          </h2>
          <p className="text-muted-foreground mb-6">
            Get access to our in-depth email deliverability research, benchmarks, and actionable strategies.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-1.5">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
              />
              {errors.fullName && (
                <p className="text-destructive text-sm mt-1">{errors.fullName}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
              />
              {errors.email && (
                <p className="text-destructive text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div 
              aria-hidden="true" 
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-600 text-white font-semibold hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                "Submitting..."
              ) : (
                <>
                  Access All Insights
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-muted-foreground text-center mt-4">
            By submitting, you agree to receive email updates from IT Dukes.
          </p>
        </div>
      </div>
    </div>
  );
};